import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { btnPrimary, inputCls, labelCls } from "@/lib/constants";
import { toast } from "sonner";
import { Server, PlugZap, Save, Lock } from "lucide-react";

const DEFAULT = {
  host: "",
  port: 21,
  username: "",
  password: "",
  remote_dir: "/",
  use_tls: false,
  passive: true,
  password_set: false,
};

export default function FtpConfig() {
  const [cfg, setCfg] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [editPw, setEditPw] = useState(false);

  useEffect(() => {
    api
      .get("/settings/ftp")
      .then((r) => setCfg({ ...DEFAULT, ...r.data }))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const upd = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const payload = () => ({
    host: cfg.host.trim(),
    port: Number(cfg.port) || 21,
    username: cfg.username.trim(),
    password: editPw ? cfg.password : "", // empty => keep existing
    remote_dir: cfg.remote_dir || "/",
    use_tls: !!cfg.use_tls,
    passive: !!cfg.passive,
  });

  const save = async () => {
    if (!cfg.host.trim()) { toast.error("Host / IP is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/ftp", payload());
      setCfg((c) => ({ ...c, ...data, password: "" }));
      setEditPw(false);
      toast.success("FTP settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  const test = async () => {
    if (!cfg.host.trim()) { toast.error("Host / IP is required to test"); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/settings/ftp/test", payload());
      toast.success(data.message || "Connected");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test failed"); }
    setTesting(false);
  };

  if (loading) return <div className="py-24 text-center text-slate-400">Loading…</div>;

  return (
    <div className="space-y-5 max-w-2xl" data-testid="ftp-config-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight flex items-center gap-2">
          <Server size={24} className="text-emerald-600" /> FTP Server
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Credentials used by scheduled backups and the "Upload to FTP" buttons on the Data &amp; Backup page.
          Password is write-only — once saved, re-save only if you need to change it.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label className={labelCls}>Host / IP *</label>
            <input data-testid="ftp-host-input" value={cfg.host} onChange={(e) => upd({ host: e.target.value })}
                   className={inputCls} placeholder="ftp.example.com or 203.0.113.10" />
          </div>
          <div>
            <label className={labelCls}>Port</label>
            <input data-testid="ftp-port-input" type="number" value={cfg.port} onChange={(e) => upd({ port: e.target.value })}
                   className={inputCls} placeholder="21" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Username</label>
            <input data-testid="ftp-username-input" value={cfg.username} onChange={(e) => upd({ username: e.target.value })}
                   className={inputCls} placeholder="backup-user" />
          </div>
          <div>
            <label className={labelCls}>Password {cfg.password_set && !editPw && <span className="text-emerald-600 ml-1 text-[10px]">(saved)</span>}</label>
            {cfg.password_set && !editPw ? (
              <div className="flex gap-2">
                <input disabled className={`${inputCls} bg-slate-50 font-mono tracking-[0.4em]`} value="••••••••" />
                <button type="button" onClick={() => { setEditPw(true); upd({ password: "" }); }}
                        className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium" data-testid="ftp-password-edit">
                  Change
                </button>
              </div>
            ) : (
              <input data-testid="ftp-password-input" type="password" value={cfg.password}
                     onChange={(e) => upd({ password: e.target.value })} className={inputCls}
                     placeholder="Password" autoComplete="new-password" />
            )}
          </div>
        </div>

        <div>
          <label className={labelCls}>Remote Directory</label>
          <input data-testid="ftp-remote-dir-input" value={cfg.remote_dir}
                 onChange={(e) => upd({ remote_dir: e.target.value })} className={inputCls}
                 placeholder="/backups/arjun-fintech" />
          <p className="text-[11px] text-slate-400 mt-1">Will be created on the server if it does not exist.</p>
        </div>

        <div className="flex flex-wrap gap-4 pt-1">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!cfg.use_tls} onChange={(e) => upd({ use_tls: e.target.checked })}
                   className="w-4 h-4 accent-emerald-600" data-testid="ftp-use-tls-input" />
            <Lock size={13} className="text-emerald-600" /> Use FTPS (explicit TLS)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!cfg.passive} onChange={(e) => upd({ passive: e.target.checked })}
                   className="w-4 h-4 accent-emerald-600" data-testid="ftp-passive-input" />
            Passive mode (recommended)
          </label>
        </div>
      </div>

      <div className="flex gap-3 justify-end">
        <button data-testid="ftp-test-button" onClick={test} disabled={testing}
                className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium text-sm inline-flex items-center gap-2 disabled:opacity-60">
          <PlugZap size={16} /> {testing ? "Testing…" : "Test Connection"}
        </button>
        <button data-testid="ftp-save-button" onClick={save} disabled={saving} className={btnPrimary}>
          <Save size={16} /> {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>
    </div>
  );
}
